// src/components/RouteErrorBoundary.jsx
// Route-level error boundary that catches page-level crashes while
// keeping the sidebar and navigation functional.
// Auto-resets when the user navigates to a different route (via key={pathname}).

import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AlertTriangle, Home, RefreshCw } from 'lucide-react';
import { reportCrash } from '../utils/crashReporter';

// ── Class boundary (error boundaries require lifecycle methods) ───────
class RouteErrorBoundaryInner extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[RouteErrorBoundary]', error, errorInfo);
    reportCrash(error, { componentStack: errorInfo?.componentStack });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center min-h-[60vh] p-6">
          <div className="w-full max-w-md">
            <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-8 text-center">
              {/* Icon */}
              <div className="inline-flex items-center justify-center w-14 h-14 bg-red-50 rounded-full mb-4">
                <AlertTriangle size={28} className="text-red-500" />
              </div>

              {/* Message */}
              <h2 className="text-lg font-bold text-slate-800 mb-2">
                This page ran into a problem
              </h2>
              <p className="text-sm text-slate-500 mb-6">
                Something unexpected happened while loading this page. You can navigate to a
                different page or reload.
              </p>

              {/* Dev-only error details */}
              {process.env.NODE_ENV === 'development' && this.state.error && (
                <details className="mb-6 text-left text-xs bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <summary className="cursor-pointer font-semibold text-slate-600 mb-1">
                    Error Details
                  </summary>
                  <pre className="whitespace-pre-wrap break-words text-red-600 overflow-auto max-h-40">
                    {this.state.error.toString()}
                    {this.state.error.stack && `\n\n${this.state.error.stack}`}
                  </pre>
                </details>
              )}

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  onClick={() => this.props.onNavigate('/')}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium text-sm rounded-lg transition-colors"
                >
                  <Home size={16} />
                  Dashboard
                </button>
                <button
                  onClick={() => window.location.reload()}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-violet-600 hover:bg-violet-700 text-white font-medium text-sm rounded-lg transition-colors"
                >
                  <RefreshCw size={16} />
                  Reload Page
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// ── Functional wrapper (provides location + navigate) ────────────────
// When location.pathname changes, React sees a new `key` on the class
// component, unmounts the old instance, and mounts a fresh one with
// hasError: false. This is the automatic reset on navigation.
export default function RouteErrorBoundary({ children }) {
  const location = useLocation();
  const navigate = useNavigate();

  return (
    <RouteErrorBoundaryInner key={location.pathname} onNavigate={navigate}>
      {children}
    </RouteErrorBoundaryInner>
  );
}
